/**
 * This code manages a product size selection interface for a product page.
 * It handles displaying product sizes based on the selected size scale (e.g., US, UK, EU),
 * updates labels and dropdown options accordingly, and saves the selected size scale
 * to cart attributes for persistence.
 */

document.addEventListener('DOMContentLoaded', () => {
  // =============================================================================
  // INITIAL SETUP
  // =============================================================================

  // Get the main product container with the currently active gender
  const productContainer = document.querySelector('.js-main-product [data-active-gender]');
  if (!productContainer) return;

  const productType = productContainer.getAttribute('data-product-type');

  // Get the size variant elements (if present)
  const sizeVariants = productContainer.querySelector('.variant-options__size');
  const sizeLabel = sizeVariants?.querySelector('.size-option_label');
  if (!sizeLabel) return;

  // Get references to the size scale dropdown elements
  const dropdownButton = sizeVariants?.querySelector('#product-size-dropdown-button');
  const dropdownOptions = sizeVariants?.querySelector('#product-size-dropdown-options');

  // =============================================================================
  // INITIAL SIZE DISPLAY SETUP
  // =============================================================================

  if (productType === 'Footwear') {
    // Determine the initial selected size scale location
    const initialOption = dropdownOptions?.querySelector('.product-size-dropdown__option--selected');
    const sizeScaleLocation = initialOption ? initialOption.dataset.countryCode : 'US';

    if (dropdownOptions) {
      dropdownButton.dataset.selectedLocation = sizeScaleLocation;
    }

    // Update the display with the chosen size scale
    updateDisplayedSize(productContainer, sizeLabel, sizeScaleLocation);

    const sizeOptionSpan = document.querySelector('#product-size-dropdown-button .size-option_label');
    if (sizeOptionSpan) {
      const initialSizeSelectedEvent = new CustomEvent('sizeSelected', {
        detail: { labelText: sizeOptionSpan.textContent }
      });
      document.dispatchEvent(initialSizeSelectedEvent);
    }

    // Save the chosen location to cart attributes
    saveLocationSelection(sizeScaleLocation, false);

  } else {
    // For non-Footwear products, just update displayed size without a location
    updateDisplayedSize(productContainer, sizeLabel, '');
  }

  // =============================================================================
  // EVENT LISTENERS (DROPDOWN)
  // =============================================================================

  // Toggle the dropdown when the button is clicked
  dropdownButton?.addEventListener('click', toggleDropdown);

  // Close the dropdown if "Escape" is pressed while it's open
  document.addEventListener('keydown', (event) => {
    const isOpen = dropdownOptions && dropdownOptions.style.display === 'block';
    if (isOpen && event.key === 'Escape') {
      closeDropdown();
      dropdownButton.focus();
    }
  });

  // Handle option selection when clicking within the dropdown
  document.addEventListener('click', (event) => {
    const listbox = document.querySelector('#product-size-dropdown-options');
    if (listbox && listbox.contains(event.target)) {
      const option = event.target.closest('li[role="option"]');
      if (option) selectOption(option);
    }
  });

  // Handle option selection with the "Enter" key when focused on an option
  document.addEventListener('keydown', (event) => {
    const openDropdown = document.getElementById('product-size-dropdown-options');
    const isOpen = openDropdown && openDropdown.style.display === 'block';

    if (isOpen && event.key === 'Enter') {
      const focusedOption = document.activeElement;
      if (focusedOption && focusedOption.getAttribute('role') === 'option') {
        selectOption(focusedOption);
      }
    }
  });

});


/* =============================================================================
   DROPDOWN FUNCTIONS
   These functions handle opening, closing, and selecting options in the dropdown.
   ============================================================================= */

function toggleDropdown() {
  const button = document.getElementById('product-size-dropdown-button');
  const dropdown = document.getElementById('product-size-dropdown-options');
  const isExpanded = button.getAttribute('aria-expanded') === 'true';

  // Toggle ARIA attribute and display style
  button.setAttribute('aria-expanded', String(!isExpanded));
  dropdown.style.display = isExpanded ? 'none' : 'block';

  // If opening the dropdown, focus the first option
  if (!isExpanded) {
    const firstOption = dropdown.querySelector('li[role="option"]');
    if (firstOption) firstOption.focus();
  }
}

function selectOption(element) {
  const button = document.getElementById('product-size-dropdown-button');
  const dropdown = document.getElementById('product-size-dropdown-options');

  // Get the newly selected location from the element
  const selectedLocation = element.dataset.countryCode;
  button.dataset.selectedLocation = selectedLocation;

  // Update size label inside the dropdown button
  const productContainer = document.querySelector('.js-main-product [data-active-gender]');
  const activeGender = productContainer.dataset.activeGender;
  const sizeOptionSpan = button.querySelector('.size-option_label');
  sizeOptionSpan.textContent = `${activeGender}'s ${selectedLocation}`;

  // Close the dropdown after selecting an option
  button.setAttribute('aria-expanded', 'false');
  dropdown.style.display = 'none';

  // Update the displayed size and save the chosen location
  const sizeVariants = productContainer.querySelector('.variant-options__size');
  const sizeLabel = sizeVariants?.querySelector('.size-option_label');
  updateDisplayedSize(productContainer, sizeLabel, selectedLocation);
  saveLocationSelection(selectedLocation, true);

  // After updating sizes, dispatch an event to notify that size scale changed
  const sizeUpdateEvent = new CustomEvent('sizeScaleUpdated', {
    detail: { location: selectedLocation }
  });
  document.dispatchEvent(sizeUpdateEvent);

  // Dispatch an event with the updated size label text
  const sizeSelectedEvent = new CustomEvent('sizeSelected', {
    detail: { labelText: sizeOptionSpan.textContent }
  });
  document.dispatchEvent(sizeSelectedEvent);

  // Update the selected class for visual feedback
  dropdown.querySelectorAll('li[role="option"]').forEach((opt) => {
    opt.classList.remove('product-size-dropdown__option--selected');
  });
  element.classList.add('product-size-dropdown__option--selected');

  // trigger change event on selected size after changing the sizing location
  document.querySelector('.size-inputs-wrapper')?.querySelector('input:checked')?.dispatchEvent(new Event('change', {bubbles:true}));
}

function closeDropdown() {
  const button = document.getElementById('product-size-dropdown-button');
  const dropdown = document.getElementById('product-size-dropdown-options');

  button.setAttribute('aria-expanded', 'false');
  dropdown.style.display = 'none';
}


/* =============================================================================
   GENDER & SIZE HELPER FUNCTIONS
   Functions to handle gender translations and formatting of sizes.
   ============================================================================= */

function getOppositeGender(gender) {
  // Returns the opposite gender for footwear sizing context
  switch (gender.toLowerCase()) {
    case 'men':
      return 'women';
    case 'women':
      return 'men';
    default:
      return 'unisex';
  }
}

function getGenderInitial(gender) {
  // Returns a single-letter representation of the gender
  switch (gender.toLowerCase()) {
    case 'men':
      return 'M';
    case 'women':
      return 'W';
    default:
      return 'U';
  }
}


/* =============================================================================
   SIZE DISPLAY & UPDATE FUNCTIONS
   The updateDisplayedSize function has two main responsibilities:
   1. Update the size label text shown to the user based on the selected location.
   2. Update the displayed size values (for each option) to reflect the chosen size scale.
   ============================================================================= */

function updateDisplayedSize(productContainer, sizeLabel, selectedLocation) {
  const sizeOptionName = document.querySelector('fieldset[data-size-option]')?.dataset.sizeOption || 'Size';
  const productType = productContainer.getAttribute('data-product-type');
  const activeGender = productContainer.dataset.activeGender;
  const sizeVariants = productContainer.querySelector('.variant-options__size');
  const checkedInput = sizeVariants?.querySelector('input[name="' + sizeOptionName + '"]:checked') ||
                     sizeVariants?.querySelector('input[name="Size"]:checked');
  if (!checkedInput) return;

  // Get the displayed value based on the currently checked input and selection
  const displayValueForSelected = getSizeValue(checkedInput, productType, activeGender, selectedLocation);

  // ---------------------------------------------------------------------------
  // PART 1: Update the size label text displayed to the user
  // ---------------------------------------------------------------------------
  if (productType === 'Footwear') {
    if (activeGender === 'women') {
      sizeLabel.textContent = window.genderStrings.women +` ${selectedLocation} ${displayValueForSelected}`;
    } else if (activeGender === 'men') {
      sizeLabel.textContent = window.genderStrings.men +` ${selectedLocation} ${displayValueForSelected}`;
    } else {
      sizeLabel.textContent = displayValueForSelected;
    }

    // Update hidden inputs for other-country-size-scale
    const sizeCountryCodeInputs = document.querySelectorAll('.product-property_other-country-size-scale');
    if (sizeCountryCodeInputs.length > 0) {
      sizeCountryCodeInputs.forEach((inputElement) => {
        inputElement.value = `${sizeOptionName} ${selectedLocation}`.trim();
      });
    } else {
      console.warn('Input with selector .product-property_other-country-size-scale not found.');
    }

    // Update inputs with combined gender sizes for other-country-size-value
    const oppositeGender = getOppositeGender(activeGender);
    const oppositeDisplayValue = getSizeValue(checkedInput, productType, oppositeGender, selectedLocation);
    const activeGenderInitial = getGenderInitial(activeGender);
    const oppositeGenderInitial = getGenderInitial(oppositeGender);

    const formattedSizeString = `${activeGenderInitial} ${displayValueForSelected} / ${oppositeGenderInitial} ${oppositeDisplayValue}`.trim();
    const otherCountrySizeInputs = document.querySelectorAll('.product-property_other-country-size-value');
    if (otherCountrySizeInputs.length > 0) {
      otherCountrySizeInputs.forEach((inputElement) => {
        inputElement.value = formattedSizeString;
      });
    } else {
      console.warn('No inputs found with selector .product-property_other-country-size-value.');
    }

  } else {
    // Non-footwear products just display the value directly
    sizeLabel.textContent = displayValueForSelected;
  }

  // ---------------------------------------------------------------------------
  // PART 2: Update the displayed size values for each available size option
  // to reflect the chosen size scale.
  // ---------------------------------------------------------------------------
  const allSizeInputs = sizeVariants.querySelectorAll('input[name="' + sizeOptionName + '"]');
  allSizeInputs.forEach(input => {
    const newValue = getSizeValue(input, productType, activeGender, selectedLocation);
    const label = input.nextElementSibling;
    if (label && label.querySelector('.size_value')) {
      label.querySelector('.size_value').textContent = newValue;
    }
  });
}

function getSizeValue(input, productType, activeGender, selectedLocation) {
  // Determines the displayed size value based on product type, gender, and location.
  if (productType !== 'Footwear') return input.value;

  const baseAttr = (activeGender === 'women')
    ? 'sizeWomen'
    : (activeGender === 'men' ? 'sizeMen' : null);

  if (!baseAttr) return input.value;

  const locationKey = capitalizeFirst(selectedLocation.toLowerCase());
  const attrWithLocation = baseAttr + locationKey;

  let sizeValue = input.dataset[attrWithLocation];
  if (!sizeValue) {
    sizeValue = input.dataset[baseAttr];
  }

  return sizeValue || input.value;
}

function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}


/* =============================================================================
   DATA PERSISTENCE
   Saves the selected location to cart attributes.
   ============================================================================= */

function saveLocationSelection(selectedLocation, clearUserSizes = false) {
  const attributesToUpdate = {
    'size_country_code': selectedLocation
  };

  if (clearUserSizes) {
    attributesToUpdate['user_men_size'] = '';
    attributesToUpdate['user_women_size'] = '';
  }

  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: attributesToUpdate })
  })
  .then(response => response.json())
  .then(data => {
    console.log('Size location saved to cart attributes:', data.attributes);
  })
  .catch(error => {
    console.error('Error updating cart attributes:', error);
  });
}




/* Mobile Bar Sticky

    if(sizeLabel) sizeLabel.textContent = sizeSelectedInput.value;
  }

  // update mobile size dropdown active size label
  const mobileBarSize = document.querySelector('.js-main-product .mobile-sticky-bar-wrapper .mobile-active-size');
  const mobileBarSelect = document.querySelector('.js-main-product .mobile-sticky-bar-wrapper .mobile-sticky-select');
  if(mobileBarSize && typeof mobileBarSize !== 'undefined') {
    if(isGiftCardProduct) mobileBarSize.textContent = sizeSelectedInput.dataset.value;
    if(!isGiftCardProduct && !isSingleSizeProduct) mobileBarSize.textContent = sizeLabel.textContent;
    
    if(activeGender === 'women' && !isGiftCardProduct && productType !== 'Kids Footwear') {
        mobileBarSelect.value = sizeSelectedInput.dataset.sizeWomen;
    } else if(activeGender === 'men' && !isGiftCardProduct && productType !== 'Kids Footwear') {
        mobileBarSelect.value = sizeSelectedInput.dataset.sizeMen;
    } else {
        mobileBarSelect.value = sizeSelectedInput.dataset.value;
    }
  }

    */